/**
 * AUREXIS — DASHBOARD
 * ===================
 * One screen that answers, in this order: does anything need me, what is the market doing, what did
 * the system last decide, how is it performing, and is the system itself healthy.
 *
 * Everything shown is read from the running system:
 *   market read       learning engine  /api/v2/ml/analytics/tf-introspection   (top-down, per timeframe;
 *                     the shared TopDownLadder, the same read the Journal shows)
 *   symbol            the shared symbol picker (contexts/SymbolContext), fed by candle-constructor
 *   decisions/results learning engine  /api/v2/ml/journal/entries              (figures: Journal/journalModel)
 *   drawings on disk  shape engine     /api/v2/health/persistence
 *   service health    pushed over a WebSocket by the control service (hooks/useFleetLive), with the
 *                     browser's own probe as the fallback; bus traffic over the event-bus stream
 * There is no refresh button: status is pushed, and the journal and market read are re-read when the
 * bus reports a new decision or world snapshot (and on a slow timer in case the bus is down).
 * Nothing is simulated or kept only in this page; when a source is unreachable its panel says so.
 */
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import CommandLayout from '@/layouts/CommandLayout';
import { INFRA_SERVICES, SERVICE_REGISTRY, type ServiceDefinition } from '@/config/serviceRegistry';
import { SystemBoard, LiveConnections } from '@/components/SystemBoard/SystemBoard';
import { useFleetLive } from '@/hooks/useFleetLive';
import { useSymbol, SymbolPicker } from '@/contexts/SymbolContext';
import { TopDownLadder, useTopDown } from '@/components/TopDown/TopDownLadder';
import ServiceDetailModal from './ServiceDetailModal';
import { SESSION_LOG_MAX, useSessionLog } from '@/hooks/useSessionLog';
import { API_BASE_URL, SHAPE_ENGINE_URL } from '@/utils/constants';
import {
  type JournalEntry, computeStats, dash, fmtAgo, fmtDateTime, fmtPct, fmtPrice, fmtR, isTrade, resultOf, rOf, titleCase,
} from './Journal/journalModel';
import './Journal/JournalPage.css';
import './CommandDashboard.css';
import { setVisibleInterval } from '@/utils/visibleInterval';

const LEARNING = '/svc/learning/api/v2/ml';
const SHAPES = SHAPE_ENGINE_URL || `${API_BASE_URL}/shape-engine`;
const REFRESH_MS = 30000;           // safety net only; the bus triggers re-reads

type Source<T> = { state: 'loading' | 'ok' | 'offline'; data: T | null };

interface Persistence { saving_to_disk: boolean; unsaved_shapes: number; error: string | null }

const CATEGORY_LABEL: Record<ServiceDefinition['category'], string> = {
  cognitive: 'Cognitive core', market: 'Market data', data: 'Data stores',
  infrastructure: 'Infrastructure', operations: 'Operations', admin: 'Administration',
};
async function getJson<T>(url: string): Promise<T> {
  const res = await fetch(url, { cache: 'no-store', signal: AbortSignal.timeout(15000) });
  if (!res.ok) throw new Error(String(res.status));
  return (await res.json()) as T;
}

function tone(n: number | null): string {
  return n === null ? 'dj-mute' : n > 0 ? 'dj-pos' : n < 0 ? 'dj-neg' : '';
}

export default function CommandDashboard() {
  const navigate = useNavigate();
  const live = useFleetLive();
  const { statuses, total } = live;
  const servicesOnline = live.online;
  const checking = live.settling;
  const lastCheck = live.updatedAt ? new Date(live.updatedAt) : null;
  const [selected, setSelected] = useState<ServiceDefinition | null>(null);
  const session = useSessionLog();
  const [now, setNow] = useState(Date.now());
  const [journal, setJournal] = useState<Source<JournalEntry[]>>({ state: 'loading', data: null });
  const { symbol } = useSymbol();
  const [shapes, setShapes] = useState<Source<Persistence>>({ state: 'loading', data: null });

  const load = useCallback(() => {
    getJson<{ journal?: JournalEntry[] }>(`${LEARNING}/journal/entries?limit=300&entries_only=false`)
      .then(d => setJournal({ state: 'ok', data: Array.isArray(d.journal) ? d.journal : [] }))
      .catch(() => setJournal(s => ({ state: 'offline', data: s.data })));
    getJson<Persistence>(`${SHAPES}/api/v2/health/persistence`)
      .then(d => setShapes({ state: 'ok', data: d }))
      .catch(() => setShapes({ state: 'offline', data: null }));
  }, []);

  useEffect(() => {
    load();
    const clock = setVisibleInterval(() => setNow(Date.now()), 1000);
    return () => clock();
  }, [load]);
  // Safety re-read only while the bus stream is down; while it is open the bus events below drive it.
  const busOpen = live.busOpen;
  useEffect(() => {
    if (busOpen) return;
    const refresh = window.setInterval(() => { if (!document.hidden) load(); }, REFRESH_MS);
    return () => window.clearInterval(refresh);
  }, [busOpen, load]);

  // A decision or a new world snapshot on the bus means the journal and the market read have changed.
  const newestRelevant = live.traffic.find(ev => /decision|world\.snapshot|spine\.reasoning/.test(ev.topic))?.id;
  useEffect(() => {
    if (!newestRelevant) return;
    const t = window.setTimeout(load, 800);         // let the writer finish before reading
    return () => window.clearTimeout(t);
  }, [newestRelevant, load]);
  // The market read re-reads on the same bus signal, plus the slow safety timer below.
  const [marketKey, setMarketKey] = useState(0);
  useEffect(() => {
    if (!newestRelevant) return;
    const t = window.setTimeout(() => setMarketKey(k => k + 1), 800);
    return () => window.clearTimeout(t);
  }, [newestRelevant]);
  useEffect(() => {
    if (busOpen) return;
    const t = window.setInterval(() => { if (!document.hidden) setMarketKey(k => k + 1); }, REFRESH_MS * 4);
    return () => window.clearInterval(t);
  }, [busOpen]);
  const market = useTopDown(symbol, marketKey);

  const rows = journal.data ?? [];
  const stats = useMemo(() => computeStats(rows), [rows]);
  const latest = useMemo(() => [...rows].sort((a, b) => b.at - a.at), [rows]);
  const lastDecision = latest[0] ?? null;
  const today = useMemo(() => {
    const start = new Date(); start.setHours(0, 0, 0, 0);
    return rows.filter(r => r.at * 1000 >= start.getTime()).length;
  }, [rows]);

  const groups = useMemo(() => {
    const order: ServiceDefinition['category'][] = ['cognitive', 'market', 'data', 'infrastructure', 'operations', 'admin'];
    const all = [...SERVICE_REGISTRY, ...INFRA_SERVICES];
    return order.map(category => {
      const services = all.filter(s => s.category === category);
      const up = services.filter(s => statuses[s.id] === 'UP').length;
      return { category, services, up };
    }).filter(g => g.services.length > 0);
  }, [statuses]);
  const down = useMemo(
    () => [...SERVICE_REGISTRY, ...INFRA_SERVICES].filter(s => statuses[s.id] === 'DOWN'),
    [statuses],
  );
  // "Stopped" is about the application services. Data stores answering on their own (Postgres,
  // Redis...) do not mean the system is running.
  const appsUp = useMemo(() => SERVICE_REGISTRY.filter(s => statuses[s.id] === 'UP').length, [statuses]);
  const fleetStopped = !checking && appsUp <= 2;

  // What needs the operator, most serious first. Each item says what is wrong and where to go.
  const attention = useMemo(() => {
    const items: { level: 'bad' | 'warn'; title: string; text: string; to: string; action: string }[] = [];
    if (fleetStopped) {
      items.push({ level: 'bad', title: 'The system is not running',
        text: `Only ${servicesOnline} of ${total} services answer. Nothing is being read, decided or learned.`,
        to: '/settings', action: 'See services' });
    } else if (down.length) {
      items.push({ level: 'warn', title: `${down.length} ${down.length === 1 ? 'service is' : 'services are'} not answering`,
        text: down.slice(0, 6).map(s => s.name).join(', ') + (down.length > 6 ? ` and ${down.length - 6} more` : ''),
        to: '/settings', action: 'See services' });
    }
    if (shapes.data && (!shapes.data.saving_to_disk || shapes.data.unsaved_shapes > 0)) {
      items.push({ level: 'bad', title: 'Drawings are not being saved to disk',
        text: `${shapes.data.unsaved_shapes} held in memory (${shapes.data.error || 'the shape database is unreachable'}). Do not restart the shape engine.`,
        to: '/chart', action: 'Open chart' });
    }
    if (!fleetStopped && journal.state === 'offline') {
      items.push({ level: 'warn', title: 'The learning engine is not answering',
        text: 'Decisions, results and the market read cannot be shown until it is back.', to: '/settings', action: 'See services' });
    }
    if (stats.open > 0) {
      items.push({ level: 'warn', title: `${stats.open} open ${stats.open === 1 ? 'trade' : 'trades'}`,
        text: 'Not graded yet — waiting for target or stop.', to: '/journal', action: 'Open journal' });
    }
    return items;
  }, [fleetStopped, servicesOnline, total, down, shapes.data, journal.state, stats.open]);

  const td = market.data;
  const fleetState = checking && !lastCheck ? 'loading' : fleetStopped ? 'offline' : 'live';

  return (
    <CommandLayout>
      <div className="dj">
        <header className="dj-head">
          <div>
            <h1 className="dj-title">Dashboard</h1>
            <p className="dj-sub">What needs you, what the market is doing, what the system decided, and how it is performing.</p>
          </div>
          <div className="dj-actions">
            <SymbolPicker />
            <span className="dj-status" data-state={fleetState} role="status">
              <span className="dj-dot" aria-hidden />
              {fleetState === 'loading' ? 'Checking services' : fleetStopped ? `System stopped · ${servicesOnline}/${total}` : `Running · ${servicesOnline}/${total} services`}
              {lastCheck ? ` · ${lastCheck.toLocaleTimeString()}` : ''}
            </span>
          </div>
        </header>

        <section className="dj-panel db-attn" aria-label="Needs attention">
          <div className="dj-panel-head">
            <h2 className="dj-panel-title">Needs attention</h2>
            <span className="dj-panel-meta">{attention.length ? `${attention.length} ${attention.length === 1 ? 'item' : 'items'}` : ''}</span>
          </div>
          {attention.length === 0 && (fleetState === 'loading' || journal.state === 'loading') ? (
            <div className="db-clear dj-mute">Checking the system…</div>
          ) : attention.length === 0 ? (
            <div className="db-clear"><span className="db-attn-dot" aria-hidden />Nothing needs you right now.</div>
          ) : (
            <ul className="db-attn-list">
              {attention.map(item => (
                <li className="db-attn-item" data-level={item.level} key={item.title}>
                  <span className="db-attn-dot" aria-hidden />
                  <div><div className="db-attn-title">{item.title}</div><div className="db-attn-text">{item.text}</div></div>
                  <button className="dj-btn" onClick={() => navigate(item.to)}>{item.action}</button>
                </li>
              ))}
            </ul>
          )}
        </section>

        <section className="dj-panel" style={{ marginBottom: 16 }} aria-label="System board">
          <div className="dj-panel-head">
            <h2 className="dj-panel-title">System board</h2>
            <span className="dj-panel-meta">every service and the connections between them, live · click a chip for detail</span>
          </div>
          <div className="sb-wrap">
            <div>
              <div className="sb-stage"><SystemBoard statuses={statuses} lastSeen={live.lastSeen} traffic={live.traffic} onSelect={setSelected} /></div>
              <div className="sb-legend">
                <span><i data-k="bus" />on the event bus, traffic seen in the last minute</span>
                <span><i data-k="direct" />direct data path, its topic seen in the last minute</span>
                <span><i />idle (both ends answer, no traffic seen) or broken</span>
                <span><b style={{ background: '#fde047' }} />just published</span>
                <span><b style={{ background: '#a78bfa' }} />its data store answers</span>
              </div>
            </div>
            <LiveConnections statuses={statuses} transport={live.transport} socketOpen={live.socketOpen}
              busOpen={live.busOpen} traffic={live.traffic} updatedAt={live.updatedAt} lastSeen={live.lastSeen} />
          </div>
        </section>

        <div className="db-cols">
          <section className="dj-panel" aria-label="Market read">
            <div className="dj-panel-head">
              <h2 className="dj-panel-title">Market read · {symbol}</h2>
              <span className="dj-panel-meta">top-down, highest timeframe first{market.state === 'offline' && td ? ' · last read (engine not answering)' : ''}</span>
            </div>
            {!td ? (
              <div className="dj-empty-inline" style={{ padding: '32px 16px' }}>
                {market.state === 'loading' ? 'Reading the market…' : `No market read available for ${symbol}: the learning engine or the candle store is not answering.`}
              </div>
            ) : (
              <TopDownLadder read={td} />
            )}
          </section>

          <section className="dj-panel" aria-label="Latest decision">
            <div className="dj-panel-head">
              <h2 className="dj-panel-title">Latest decision</h2>
              <button className="db-link" onClick={() => navigate('/journal')}>Open journal</button>
            </div>
            {!lastDecision ? (
              <div className="dj-empty-inline" style={{ padding: '32px 16px' }}>
                {journal.state === 'loading' ? 'Loading…' : journal.state === 'offline' ? 'The journal cannot be read — the learning engine is not answering.' : 'No decisions recorded yet.'}
              </div>
            ) : (
              <div className="db-decision">
                <div className="db-decision-top">
                  <span className="dj-tag" data-tone={lastDecision.action}>{(lastDecision.action || '?').toUpperCase()}</span>
                  <strong>{lastDecision.symbol}</strong>
                  <span className="dj-mute">{lastDecision.timeframe}{lastDecision.style ? ` · ${titleCase(lastDecision.style)}` : ''}</span>
                  <span className="db-decision-when" title={fmtDateTime(lastDecision.at)}>{fmtAgo(lastDecision.at, now)}</span>
                </div>
                {isTrade(lastDecision) && (
                  <dl className="db-levels">
                    <div><dt>Entry</dt><dd>{fmtPrice(lastDecision.entry)}</dd></div>
                    <div><dt>Stop</dt><dd className="dj-neg">{fmtPrice(lastDecision.stop)}</dd></div>
                    <div><dt>Target</dt><dd className="dj-pos">{fmtPrice(lastDecision.target)}</dd></div>
                    <div><dt>Result</dt><dd className={tone(rOf(lastDecision))}>{resultOf(lastDecision) === 'open' ? 'Open' : fmtR(rOf(lastDecision))}</dd></div>
                  </dl>
                )}
                <p className="db-why" style={{ marginTop: isTrade(lastDecision) ? 0 : 14 }}>{lastDecision.because || 'No reason was recorded.'}</p>
              </div>
            )}
          </section>
        </div>

        <section className="dj-kpis" aria-label="Results">
          <Kpi label="Decisions today" value={journal.data ? String(today) : dash} note={journal.data ? `${stats.decisions} recorded in all` : 'journal unavailable'} />
          <Kpi label="Open trades" value={journal.data ? String(stats.open) : dash} note={`${stats.graded} graded`} />
          <Kpi label="Win rate" value={fmtPct(stats.winRate)} note={stats.graded ? `${stats.wins} won · ${stats.losses} lost` : 'no graded trades yet'} />
          <Kpi label="Net result" value={fmtR(stats.netR)} className={tone(stats.netR)} note="in units of risk (R)" />
          <Kpi label="Expectancy" value={fmtR(stats.expectancy)} className={tone(stats.expectancy)} note="average R per graded trade" />
        </section>

        <div className="db-cols">
          <section className="dj-panel" aria-label="System">
            <div className="dj-panel-head">
              <h2 className="dj-panel-title">System</h2>
              <button className="db-link" onClick={() => navigate('/settings')}>All services</button>
            </div>
            {groups.map(g => {
              const share = g.up / g.services.length;
              return (
                <div className="db-sys-row" key={g.category}>
                  <span>{CATEGORY_LABEL[g.category]}</span>
                  <span className="db-bar" data-state={share === 1 ? 'all' : share === 0 ? 'none' : 'part'} aria-hidden><span style={{ width: `${share * 100}%` }} /></span>
                  <span className="db-sys-count">{g.up}/{g.services.length}</span>
                </div>
              );
            })}
            {!fleetStopped && down.length > 0 && (
              <div className="db-down"><b>Not answering:</b> {down.map(s => s.name).join(', ')}</div>
            )}
          </section>

          <section className="dj-panel" aria-label="Recent decisions">
            <div className="dj-panel-head">
              <h2 className="dj-panel-title">Recent decisions</h2>
              <span className="dj-panel-meta">{latest.length ? `last ${Math.min(6, latest.length)} of ${latest.length}` : ''}</span>
            </div>
            {latest.length === 0 ? (
              <div className="dj-empty-inline" style={{ padding: '32px 16px' }}>{journal.state === 'offline' ? 'Journal unavailable.' : 'Nothing recorded yet.'}</div>
            ) : (
              <table className="db-recent">
                <tbody>
                  {latest.slice(0, 6).map(r => {
                    const res = resultOf(r);
                    return (
                      <tr key={`${r.decision_id}:${r.at}`} onClick={() => navigate('/journal')}>
                        <td className="dj-mute">{fmtAgo(r.at, now)}</td>
                        <td><span className="dj-tag" data-tone={r.action}>{(r.action || '?').toUpperCase()}</span></td>
                        <td>{r.symbol} <span className="dj-mute">{r.timeframe}</span></td>
                        <td>{res === 'none' ? <span className="dj-mute">{dash}</span> : <span className="dj-tag" data-tone={res}>{res === 'won' ? 'Target hit' : res === 'lost' ? 'Stopped' : res === 'open' ? 'Open' : res === 'expired' ? 'Expired' : res === 'ambiguous' ? 'Ambiguous' : 'Break-even'}</span>}</td>
                        <td className={tone(rOf(r))}>{fmtR(rOf(r))}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            )}
          </section>
        </div>

        <footer className="db-foot">
          <span>This session: {session.fmtSecs(session.sessSecs)}</span>
          <button className="db-link" onClick={session.openSessionHistory}>Session history</button>
          <div className="db-links">
            <button className="dj-btn" onClick={() => navigate('/chart')}>Chart</button>
            <button className="dj-btn" onClick={() => navigate('/learning')}>Learning</button>
            <button className="dj-btn" onClick={() => navigate('/monitor')}>Monitor</button>
          </div>
        </footer>

        {selected && <ServiceDetailModal service={selected} statuses={statuses} onClose={() => setSelected(null)} />}

        {session.showSessionHistory && (
          <div className="db-backdrop" onClick={() => session.setShowSessionHistory(false)}>
            <div className="db-modal" role="dialog" aria-modal="true" aria-labelledby="db-history" onClick={e => e.stopPropagation()}>
              <h3 id="db-history">Session history</h3>
              <p>Kept in this browser only, not on any server. Last {SESSION_LOG_MAX} sessions; each is written once and closed once.</p>
              <div className="db-history">
                {session.sessionLog.length === 0 ? <div className="dj-empty-inline">No sessions recorded yet.</div> : session.sessionLog.map(entry => {
                  const current = entry.id === session.currentSessionId;
                  const open = entry.endedAt === null;
                  return (
                    <div className="db-history-row" key={entry.id}>
                      <span>{new Date(entry.startedAt).toLocaleString()}</span>
                      <span className="dj-mute">{open ? (current ? 'Active' : 'Not closed cleanly') : 'Ended'}</span>
                      <strong>{open ? (current ? session.fmtSecs(session.sessSecs) : dash) : session.fmtSessionDuration(entry.startedAt, entry.endedAt)}</strong>
                    </div>
                  );
                })}
              </div>
              <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: 14 }}>
                <button className="dj-btn" onClick={() => session.setShowSessionHistory(false)}>Close</button>
              </div>
            </div>
          </div>
        )}
      </div>
    </CommandLayout>
  );
}

function Kpi({ label, value, note, className }: { label: string; value: string; note?: string; className?: string }) {
  return (
    <div className="dj-kpi">
      <div className="dj-kpi-label">{label}</div>
      <div className={`dj-kpi-value ${value === dash ? 'dj-mute' : className ?? ''}`}>{value}</div>
      {note && <div className="dj-kpi-note" title={note}>{note}</div>}
    </div>
  );
}
